import { NightShiftAvatar, PersonaMessages, useName } from '@bechtel/client-common';
import { Avatar, AvatarSize, Box, Icon, IconSize, Text } from '@bechtel/gds';
import { CrewMemberAssignment, CrewMemberShiftType, PersonaType } from '@bechtel/types';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { compact, filter, flatten, groupBy, map, orderBy, uniq, uniqBy } from 'lodash';
import { FC, Fragment } from 'react';
import { AddUserStyled, SelectItemStyled, SeparatorStyled } from './styled';
import { OwnerListProps } from './types';

const getSupervisorIds = (persona: PersonaType, ownersByPersona: CrewMemberAssignment[][]) => {
  if (!(ownersByPersona || PersonaType.FOREMAN || PersonaType.GENERAL_FOREMAN)) return null;
  const supervisors = filter(flatten(ownersByPersona), (owner) =>
    persona === PersonaType.FOREMAN
      ? owner?.persona === PersonaType.GENERAL_FOREMAN
      : owner?.persona === PersonaType.SUPERINTENDENT
  );
  return uniq(
    map(supervisors, (supervisor) => {
      return supervisor.crewMember?.id;
    })
  );
};

export const OwnerList: FC<OwnerListProps> = ({
  assignedCrewMembers,
  onChange,
  canEditSuperintendent,
  canEditGeneralForeman,
  canEditForeman,
  canEditFieldEngineer,
}) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();
  const ownersByPersona = Object.values(groupBy(assignedCrewMembers, 'persona'));
  const listOrder = [
    PersonaType.SUPERINTENDENT,
    PersonaType.GENERAL_FOREMAN,
    PersonaType.FOREMAN,
    PersonaType.FIELD_ENGINEER,
  ];
  const orderedOwnersByPersona = orderBy(ownersByPersona, (owners) => listOrder.indexOf(owners[0]?.persona));

  return (
    <>
      {map(orderedOwnersByPersona, (owners: CrewMemberAssignment[], index) => {
        const uniquePersona = owners.length > 0 ? uniqBy(owners, 'crewMember.id') : [];
        const isMultiplePersonaAssigned = uniquePersona.length > 1;
        const crewMemberAssignment = uniquePersona[0];
        const ownerPersona = owners[0]?.persona;
        const supervisorIds = compact(getSupervisorIds(ownerPersona, ownersByPersona));
        // TODO : Differentation between Primary and secondary owner responsibilities will be handled in a different ticket
        const hadEditPermission =
          (ownerPersona === PersonaType.SUPERINTENDENT && canEditSuperintendent) ||
          (ownerPersona === PersonaType.FIELD_ENGINEER && canEditFieldEngineer) ||
          (ownerPersona === PersonaType.GENERAL_FOREMAN && canEditGeneralForeman) ||
          (ownerPersona === PersonaType.FOREMAN && canEditForeman);
        //can edit user only if ONE supervisor is assigned for General Foreman and Foreman
        const addUserEnabled =
          hadEditPermission &&
          (ownerPersona === PersonaType.SUPERINTENDENT ||
            ownerPersona === PersonaType.FIELD_ENGINEER ||
            supervisorIds.length === 1);

        const supervisorId =
          (ownerPersona === PersonaType.FOREMAN || ownerPersona === PersonaType.GENERAL_FOREMAN) &&
            supervisorIds.length === 1
            ? supervisorIds[0]
            : null;

        const persona = crewMemberAssignment ? crewMemberAssignment.persona : owners[0] && owners[0].persona;
        return (
          <Fragment key={index}>
            <SelectItemStyled
              onClick={() => {
                addUserEnabled && onChange(owners, supervisorId);
              }}
              flexGrow={{ sm: 1 }}
              px={{ sm: '500' }}
              data-testid="assigned-crew-member"
              canAdd={addUserEnabled}
            >
              <Box mr={{ sm: '500' }}>
                {!isMultiplePersonaAssigned &&
                  (crewMemberAssignment?.crewMember ? (

                    (crewMemberAssignment?.crewMember.shiftType === CrewMemberShiftType.NIGHT ? (
                      <NightShiftAvatar
                        src={crewMemberAssignment?.crewMember.imageUrl ?? ''}
                        data-testid="night-shift-avatar"
                      />
                    ) : (
                      <Avatar
                        size={AvatarSize.MEDIUM}
                        src={crewMemberAssignment?.crewMember.imageUrl ?? ''}
                        data-testid="assigned-user-photo"
                        style={{ borderWidth: '0px' }}
                      />
                    ))

                  ) : (
                    <AddUserStyled>
                      <Icon size={IconSize.LARGE} icon="user-plus" color="white" />
                    </AddUserStyled>
                  ))}
                {isMultiplePersonaAssigned && <Icon size={IconSize.LARGE} icon="users" />}
              </Box>
              <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                <Box mb={{ sm: '300' }}>
                  <Text size="400" weight="600" color="grey.1000" data-testid="assigned-user-name">
                    {isMultiplePersonaAssigned
                      ? t`Multiple Assigned`
                      : crewMemberAssignment && crewMemberAssignment.crewMember
                        ? getFullName(
                          crewMemberAssignment.crewMember.firstName,
                          crewMemberAssignment.crewMember.lastName
                        )
                        : t`Add User`}
                  </Text>
                </Box>

                <Text size="300" color="grey.600" data-testid="assigned-user-role">
                  {i18n._(PersonaMessages[persona])}
                </Text>
              </Box>
              {addUserEnabled && (
                <Icon
                  fillColor="bechtel.blue.800"
                  icon={crewMemberAssignment && crewMemberAssignment.crewMember ? 'edit' : 'plus'}
                  size={IconSize.MEDIUM}
                  data-testid={`user-edit-icon-${persona}`}
                ></Icon>
              )}
            </SelectItemStyled>
            <SeparatorStyled></SeparatorStyled>
          </Fragment>
        );
      })}
    </>
  );
};
